import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const RaiseTicket = () => {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [enterprises, setEnterprises] = useState([]);
  const [form, setForm] = useState({
    enterpriseId: searchParams.get('enterprise') || '',
    description: '',
    ticketType: '',
    priority: 'MEDIUM',
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/api/kam/${user.userId}/enterprises`)
      .then(res => setEnterprises(res.data))
      .catch(() => toast.error('Failed to load enterprises'));
  }, [user.userId]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.enterpriseId) { toast.error('Please select an enterprise'); return; }
    if (!form.description.trim()) { toast.error('Please enter a description'); return; }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('enterpriseId', form.enterpriseId);
      formData.append('description', form.description);
      formData.append('priority', form.priority);
      formData.append('kamId', user.userId);
      if (form.ticketType) formData.append('ticketType', form.ticketType);
      if (file) formData.append('attachment', file);

      const res = await api.post('/api/kam/tickets/raise', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Ticket ${res.data.ticketId} raised! Assigned to ${res.data.assignedAgentName || 'an agent'}.`);
      setTimeout(() => navigate('/kam/tickets'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to raise ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Raise New Ticket</h1>
          <p className="text-gray-500 text-sm">AI will automatically classify and assign your ticket</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enterprise *</label>
              <select value={form.enterpriseId} onChange={e => setForm({ ...form, enterpriseId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" required>
                <option value="">Select Enterprise</option>
                {enterprises.map(e => (
                  <option key={e.enterpriseId} value={e.enterpriseId}>{e.enterpriseName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issue Type <span className="text-gray-400">(optional — AI will classify)</span>
              </label>
              <select value={form.ticketType} onChange={e => setForm({ ...form, ticketType: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="">Let AI Decide</option>
                <option value="CARD_ISSUE">Card Issue</option>
                <option value="APPROVAL_ISSUE">Approval Issue</option>
                <option value="REIMBURSEMENT_ISSUE">Reimbursement Issue</option>
                <option value="TECHNICAL_ISSUE">Technical Issue</option>
                <option value="ONBOARDING_ISSUE">Onboarding Issue</option>
                <option value="REPORT_ISSUE">Report Issue</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="Describe the issue in detail..." required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachment <span className="text-gray-400">(optional)</span>
              </label>
              <input type="file" accept="image/*,.pdf"
                onChange={e => setFile(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-primary hover:file:bg-blue-100" />
              {file && (
                <p className="text-xs text-gray-400 mt-1">Selected: {file.name}</p>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={loading}
                className="flex-1 bg-primary text-white font-semibold py-2.5 rounded-lg hover:bg-primary-light transition-colors disabled:opacity-50">
                {loading ? 'Raising Ticket (AI Processing...)' : 'Raise Ticket'}
              </button>
              <button type="button" onClick={() => navigate('/kam/home')}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RaiseTicket;
